"use client";

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { trackContactFormStep, trackContactFormSubmission, trackEvent } from '@/lib/analytics';
import styles from './ContactForm.module.css';
import Icon from './Icon';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

type Step = 'intro' | 'name' | 'email' | 'subject' | 'message' | 'confirm' | 'success' | 'error';

// Intelligent responses based on user input
const getSmartResponse = (step: Step, input: string): string => {
  const timeOfDay = new Date().getHours();
  let greeting = "Hi";
  
  if (timeOfDay < 12) greeting = "Good morning";
  else if (timeOfDay < 18) greeting = "Good afternoon";
  else greeting = "Good evening";

  switch (step) {
    case 'name':
      if (input.length < 3) return `That seems a bit short. Could you share your full name?`;
      if (input.includes(" ")) return `Good to meet you, ${input.split(" ")[0]}. What's your email address?`;
      return `Good to meet you, ${input}. What's your email address?`;
      
    case 'email':
      if (input.includes("gmail")) return `Got it. What would you like to discuss?`;
      if (input.includes("outlook") || input.includes("hotmail")) return `Noted. What's the subject of your message?`;
      return `Thanks. What's the subject of your message?`;
      
    case 'subject':
      if (input.toLowerCase().includes("help") || input.toLowerCase().includes("support")) 
        return `Understood. Tell me more about what you're looking for.`;
      if (input.toLowerCase().includes("quote") || input.toLowerCase().includes("price")) 
        return `Happy to discuss scope and pricing. Share some details about your project.`;
      if (input.toLowerCase().includes("job") || input.toLowerCase().includes("career") || input.toLowerCase().includes("work")) 
        return `Interested in working together? Great. Tell me more about the opportunity.`;
      if (input.toLowerCase().includes("dtc") || input.toLowerCase().includes("ecommerce") || input.toLowerCase().includes("e-commerce")) 
        return `DTC and e-commerce is my core focus. Share the details and I'll get back to you.`;
      return `Good. Now share the details of your message.`;
      
    case 'intro':
    default:
      return `${greeting}. Thanks for reaching out. Let's start with your name.`;
  }
};

const ContactForm: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Conversation state
  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [isTyping, setIsTyping] = useState(false);
  const [conversation, setConversation] = useState<Array<{type: 'bot' | 'user', text: string}>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Initial bot message
  useEffect(() => {
    const timer = setTimeout(() => {
      setConversation([
        { 
          type: 'bot', 
          text: getSmartResponse('intro', '') 
        }
      ]);
      setCurrentStep('name');
      setIsTyping(false);
      
      // Track form start
      trackContactFormStep('form_started', {
        timestamp: new Date().toISOString()
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Scroll to bottom of conversation - only scroll the chat container, not the page
  useEffect(() => {
    if (messagesEndRef.current) {
      const messagesContainer = document.querySelector(`.${styles.messages}`);
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  }, [conversation]);
  // Focus input field when user interacts, but not on initial load to prevent unwanted scrolling
  useEffect(() => {
    // Only focus after the initial setup is complete and user has started interacting
    if (inputRef.current && ['name', 'email', 'subject', 'message'].includes(currentStep) && conversation.length > 1) {
      // Only focus if not on mobile and user has already interacted with the form
      if (window.innerWidth > 768) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  }, [currentStep, conversation.length]);

  // Handle bot typing and responses
  const addBotMessage = (message: string, delay = 500) => {
    setIsTyping(true);
    
    setTimeout(() => {
      setConversation(prev => [...prev, { type: 'bot', text: message }]);
      setIsTyping(false);
    }, delay);
  };

  // Generate suggestions based on current step
  const getSuggestions = React.useCallback((): string[] => {
    // For debugging
    console.log("Current step:", currentStep);
    console.log("Current subject:", formData.subject);
    
    switch (currentStep) {
      case 'subject':
        return ['Project inquiry', 'Collaboration opportunity', 'Question about services'];
      case 'message':
        if (formData.subject && formData.subject.toLowerCase().includes('project')) {
          return ['I need help with a website redesign', 'Looking for SEO optimization services', 'Interested in your digital marketing packages'];
        }
        if (formData.subject && formData.subject.toLowerCase().includes('question')) {
          return ['What are your rates?', 'What is your process?', 'Do you work with international clients?'];
        }
        return ["I'd like to discuss a potential project", "Looking forward to your expertise", "Need help with my digital marketing strategy"];
      case 'confirm':
        return ['Yes, send it now', 'Edit my message'];
      default:
        return [];
    }
  }, [currentStep, formData.subject]);

  // Handle user input submission
  const handleSubmit = (e: React.FormEvent) => {
    // Prevent default behavior and stop propagation
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent page scroll
    window.scrollTo(window.scrollX, window.scrollY);
    
    if (!inputValue.trim() && currentStep !== 'confirm') {
      return;
    }
    
    // Add user message to conversation
    setConversation(prev => [...prev, { type: 'user', text: inputValue }]);
      // Process based on current step
    switch (currentStep) {
      case 'name':
        setFormData(prev => ({ ...prev, name: inputValue }));
        addBotMessage(getSmartResponse('name', inputValue));
        setCurrentStep('email');
        trackContactFormStep('name_completed', { name_length: inputValue.length });
        break;
        
      case 'email':
        // Basic email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue)) {
          addBotMessage("Hmm, that doesn't look like a valid email. Could you please try again?");
          trackContactFormStep('email_validation_failed', { email_input: inputValue.substring(0, 10) + '...' });
          return;
        }
        
        setFormData(prev => ({ ...prev, email: inputValue }));
        addBotMessage(getSmartResponse('email', inputValue));
        setCurrentStep('subject');
        trackContactFormStep('email_completed', { 
          email_domain: inputValue.split('@')[1] || 'unknown',
          email_provider: inputValue.includes('gmail') ? 'gmail' : inputValue.includes('outlook') ? 'outlook' : 'other'
        });
        break;
        
      case 'subject':
        setFormData(prev => ({ ...prev, subject: inputValue }));
        addBotMessage(getSmartResponse('subject', inputValue));
        setCurrentStep('message');
        trackContactFormStep('subject_completed', { 
          subject_length: inputValue.length,
          subject_type: inputValue.toLowerCase().includes('project') ? 'project' : 
                       inputValue.toLowerCase().includes('question') ? 'question' : 'other'
        });
        break;
          case 'message':
        if (inputValue.trim().length < 10) {
          addBotMessage("Could you provide a bit more detail? It helps me understand better.");
          trackContactFormStep('message_validation_failed', { 
            message_length: inputValue.trim().length,
            min_required: 10
          });
          return;
        }
        
        setFormData(prev => ({ ...prev, message: inputValue }));
        addBotMessage(`Perfect! Here's what I've got:
        
Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

Click the green button below to send your message or type 'edit' to make changes.`);
        setCurrentStep('confirm');
        trackContactFormStep('message_completed', { 
          message_length: inputValue.trim().length,
          message_word_count: inputValue.trim().split(/\s+/).length
        });
        break;
        
      case 'confirm':
        if (inputValue.toLowerCase().includes('send') || inputValue.toLowerCase().includes('yes') || inputValue.toLowerCase().includes('confirm')) {
          submitForm();
        } else if (inputValue.toLowerCase() === '') {
          // Empty input is fine in confirm step - user might use the button instead
          return;
        } else if (inputValue.toLowerCase().includes('edit') || inputValue.toLowerCase().includes('no')) {
          addBotMessage("No problem! Let's start over. What's your name?");
          setCurrentStep('name');
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
        } else {
          addBotMessage("I didn't catch that. Please type 'send' to confirm your message or 'edit' to make changes.");
        }
        break;
      
      // Add handlers for success and error states
      case 'success':
      case 'error':
        if (inputValue.toLowerCase() === 'new') {
          // Reset the form for a new submission
          addBotMessage("Let's start over. What's your name?");
          setCurrentStep('name');
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
          setError(null);
        } else if (inputValue.toLowerCase() === 'retry' && currentStep === 'error') {
          // Only retry if we're in error state
          addBotMessage("Let's try sending your message again...");
          submitForm(); // Try submitting the form again
        } else {
          addBotMessage("I didn't catch that. Type 'new' to start over" + 
            (currentStep === 'error' ? " or 'retry' to try sending again" : "") + ".");
        }
        break;
    }
    
    // Clear input
    setInputValue('');
  };
  // Submit form to API
  const submitForm = async () => {
    setIsSubmitting(true);
    addBotMessage("Sending your message...");
    
    const startTime = Date.now();
    const conversationLength = conversation.length;
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          message: formData.message || inputValue // Use the last input as message if not set yet
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong');
      }
      
      // Success
      setCurrentStep('success');
      addBotMessage("Message sent successfully! I'll get back to you as soon as possible. Thanks for reaching out!", 1000);
        // Track successful submission
      trackContactFormSubmission({
        source: 'chatbot',
        conversationLength: conversationLength,
        timeSpent: Date.now() - startTime
      });
      
      // Add option to send another message
      setTimeout(() => {
        addBotMessage("Would you like to send another message? Type 'new' to start over.");
      }, 2000);
      
    } catch (error) {
      // Error
      setCurrentStep('error');
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
      addBotMessage(`Something went wrong: ${errorMessage}. Please try again.`);
        // Track failed submission
      trackEvent('contact_form_error', {
        event_category: 'error',
        source: 'chatbot',
        conversationLength: conversationLength,
        timeSpent: Date.now() - startTime,
        error_message: errorMessage
      });
      
      // Add option to try again
      setTimeout(() => {
        addBotMessage("Type 'retry' to try again or 'new' to start over.");
      }, 1500);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    // Prevent default behavior and stop propagation
    window.scrollTo(window.scrollX, window.scrollY);
    
    // Track suggestion click
    trackEvent('suggestion_clicked', {
      suggestion_text: suggestion,
      current_step: currentStep,
      conversation_length: conversation.length
    });
    
    // Special handling for confirm step
    if (currentStep === 'confirm') {
      if (suggestion.toLowerCase().includes('yes') || suggestion.toLowerCase().includes('send')) {
        submitForm();
        return;
      } else if (suggestion.toLowerCase().includes('edit')) {
        addBotMessage("No problem! Let's start over. What's your name?");
        setCurrentStep('name');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        return;
      }
    }
    
    // For other steps
    setInputValue(suggestion);
    
    // Auto-submit after a short delay
    setTimeout(() => {
      const event = new Event('submit', { bubbles: true, cancelable: true }) as unknown as React.FormEvent;
      handleSubmit(event);
    }, 100);
  };

  // Get placeholder text based on current step
  const getPlaceholder = () => {
    switch (currentStep) {
      case 'name': return "Type your name...";
      case 'email': return "Type your email...";
      case 'subject': return "Type a subject...";
      case 'message': return "Type your message...";
      case 'confirm': return "Type 'send' to confirm or 'edit' to make changes...";
      case 'success':
      case 'error': return "Type 'new' to start over...";
      default: return "Type your message...";
    }
  };

  // Get input type based on current step
  const getInputType = () => {
    return currentStep === 'email' ? 'email' : 'text';
  };

  // Suggestions to display - ensure we're getting fresh suggestions each render
  const suggestions = React.useMemo(() => {
    const result = getSuggestions();
    console.log("Generated suggestions:", result);
    return result;
  }, [getSuggestions]); // getSuggestions is now memoized with useCallback

  return (
    <div className={styles.conversationalForm}>
      <div className={styles.chatHeader}>
        <div className={styles.chatBotInfo}>
          <div className={styles.botAvatarLarge}>
            <Image
              src="/1706599500760.avif"
              alt="Bemoi George"
              width={200}
              height={200}
              className={styles.botSvg}
            />
          </div>
          <div className={styles.botInfo}>
            <h3>Bemoi&apos;s</h3>
            <span>Digital Assistant</span>
            <div className={styles.socialLinks}>
              <a href="https://www.linkedin.com/in/bemoi/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <a href="https://www.instagram.com/bemoig" target="_blank" rel="noopener noreferrer">@bemoig</a>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.chatContainer}>
        <div className={styles.messages}>
          {conversation.map((msg, index) => (
            <div 
              key={index} 
              className={`${styles.message} ${msg.type === 'bot' ? styles.botMessage : styles.userMessage}`}
            >
              {msg.type === 'bot' && (
                <div className={styles.botAvatar}>
                  <Image
                    src="/1706599500760.avif"
                    alt="Bemoi George"
                    width={50}
                    height={50}
                    className={styles.botSvg}
                  />
                </div>
              )}
              <div className={styles.messageContent}>
                {msg.text.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < msg.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
              {msg.type === 'user' && <div className={styles.userAvatar}></div>}
            </div>
          ))}
          
          {isTyping && (
            <div className={`${styles.message} ${styles.botMessage}`}>
              <div className={styles.botAvatar}>
                <Image
                  src="/1706599500760.avif"
                  alt="Bemoi George"
                  width={50}
                  height={50}
                  className={styles.botSvg}
                />
              </div>
              <div className={styles.typingIndicator}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          
          {/* Suggestions are shown as part of the chat flow */}
          {suggestions.length > 0 && !isTyping && (
            <div className={`${styles.message} ${styles.botMessage}`}>
              <div className={styles.botAvatar}>
                <Image
                  src="/1706599500760.avif"
                  alt="Bemoi George"
                  width={50}
                  height={50}
                  className={styles.botSvg}
                />
              </div>
              <div className={styles.suggestionBubble}>
                <div className={styles.suggestionOptions}>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className={styles.suggestionButton}
                      onClick={() => handleSuggestionClick(suggestion)}
                      type="button" // Prevent form submission
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Green send confirmation button that appears only during the confirm step */}
          {currentStep === 'confirm' && !isTyping && (
            <div className={`${styles.message} ${styles.botMessage}`}>
              <div className={styles.botAvatar}>
                <Image
                  src="https://media.licdn.com/dms/image/v2/D5603AQHG0VMv3Xrq8w/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1706599500760?e=1752105600&v=beta&t=81et4uqLqmvw6Ub0nu5G2KV53A6h5JIq8B_CYLKck-k"
                  alt="Bemoi George"
                  width={50}
                  height={50}
                  className={styles.botSvg}
                />
              </div>
              <button
                type="button"
                className={styles.sendConfirmButton}
                onClick={() => submitForm()}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        
        <form
          onSubmit={handleSubmit}
          className={styles.inputArea}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            type={getInputType()}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={getPlaceholder()}
            disabled={isSubmitting || isTyping || !['name', 'email', 'subject', 'message', 'confirm', 'success', 'error'].includes(currentStep)}
            className={styles.chatInput}
            autoComplete={currentStep === 'email' ? 'email' : 'off'}
          />
          <button
            type="submit"
            className={styles.sendButton}
            disabled={isSubmitting || isTyping || !inputValue.trim()}
            aria-label="Send message"
          >
            {isSubmitting ? (
              <span className={styles.spinner}></span>
            ) : (
              <Icon name="send" size={18} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;